import React, { useState, useEffect, useRef, useCallback } from 'react';
import { PlusIcon, PencilIcon, TrashIcon, MagnifyingGlassIcon, WrenchScrewdriverIcon, CalculatorIcon, BuildingOfficeIcon, MapPinIcon, UserCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext.tsx';
import { hiresAPI, equipmentAPI, suppliersAPI, sitesAPI, zaharaAPI } from '../services/api.ts';

interface Hire {
  id: string;
  price: number;
  pricing_term: 'fixed' | 'day' | 'week' | 'month' | 'year';
  price_per_day?: number | string; // Calculated field for comparison (can be string from DB)
  hire_start_date: string;
  hire_end_date: string;
  hire_duration_days: number;
  total_cost: number;
  hire_status: 'active' | 'completed' | 'collection_booked';
  created_at: string;
  updated_at: string;
  zahara_document_id?: string;
  zahara_po_number?: string;
  delivery_charge?: number;
  operator_cost?: number;
  equipment_name: string;
  equipment_category: string;
  equipment_image_url?: string;
  supplier_name: string;
  supplier_email?: string;
  supplier_phone?: string;
  site_name: string;
  site_address: string;
  site_address_line_1?: string;
  site_address_line_2?: string;
  site_city?: string;
  site_county?: string;
  site_postcode?: string;
  site_country?: string;
  job_number?: string;
  equipment_id?: string;
  supplier_id?: string;
  site_id?: string;
  creator_name?: string;
  creator_profile_picture?: string;
}

interface Equipment {
  id: string;
  name: string;
  category: string;
  unit_of_measure: string;
  power?: string;
  image_url?: string;
}

interface Supplier {
  id: string;
  name: string;
  contact_email?: string;
  contact_phone?: string;
  address?: string;
}

interface Site {
  id: string;
  name: string;
  address: string;
  job_number?: string;
}

const Hires: React.FC = () => {
  const { t } = useTranslation();
  const { customer } = useAuth();
  
  // Render hire card content
  const renderHireCard = (hire: Hire) => (
    <div className="flex gap-6">
      {/* Selection Checkbox */}
      <div className="flex-shrink-0 flex items-start pt-1">
        <input
          type="checkbox"
          checked={selectedHires.includes(hire.id)}
          onChange={() => handleHireSelect(hire.id)}
          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
        />
      </div>
      {/* Equipment Image */}
      <div className="flex-shrink-0">
        <div className="w-24 h-24 bg-gray-100 rounded-lg overflow-hidden flex items-center justify-center">
          {hire.equipment_image_url ? (
            <img
              src={hire.equipment_image_url}
              alt={hire.equipment_name}
              className="w-full h-full object-cover"
            />
          ) : (
            <WrenchScrewdriverIcon className="h-8 w-8 text-gray-400" />
          )}
        </div>
      </div>
      
      {/* Card Content */}
      <div className="flex-1">
        <div className="flex justify-between items-start mb-4">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <h3 className="text-xl font-semibold text-gray-900">{hire.equipment_name}</h3>
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(hire.hire_status)}`}>
                {formatStatusDisplay(hire.hire_status)}
              </span>
            </div>
            <p className="text-gray-600 mb-2">{hire.equipment_category}</p>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm">
              <div>
                <span className="font-medium text-gray-700">{t('hires.supplier')}:</span>
                <p className="text-gray-600">{hire.supplier_name}</p>
              </div>
              <div>
                <span className="font-medium text-gray-700">{t('hires.site')}:</span>
                <p className="text-gray-600">{hire.site_name}</p>
                {hire.job_number && <p className="text-gray-500 text-xs">Job: {hire.job_number}</p>}
              </div>
              <div>
                <span className="font-medium text-gray-700">{t('hires.duration')}:</span>
                <p className="text-gray-600">{hire.hire_duration_days} days</p>
              </div>
              <div>
                <span className="font-medium text-gray-700">PO Number:</span>
                {hire.zahara_po_number ? (
                  <div className="flex items-center gap-1">
                    <p className="text-gray-600 font-mono text-xs">#{hire.zahara_po_number}</p>
                    <a
                      href={`https://myzahara.net/v3/documents/purchase-orders/view/${hire.zahara_document_id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-800"
                      title="View in Zahara"
                    >
                      <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                    </a>
                  </div>
                ) : (
                  <p className="text-gray-400 text-xs">No PO created</p>
                )}
              </div>
            </div>
          </div>
          <div className="text-right">
            <p className="text-2xl font-bold text-gray-900">{formatCurrency(hire.total_cost)}</p>
            <p className="text-sm text-gray-500">
              {formatCurrency(hire.total_cost / (1 + VAT_RATE))} ex VAT
            </p>
            <p className="text-sm text-gray-500">
              {formatCurrency(hire.price)}/{hire.pricing_term === 'fixed' ? 'fixed' : hire.pricing_term}
              {hire.price_per_day && (
                <span className="text-xs text-gray-400 ml-1">
                  (£{formatPricePerDay(hire.price_per_day)}/day)
                </span>
              )}
            </p>
            <p className="text-xs text-gray-400 mt-1">
              {formatDate(hire.hire_start_date)} - {formatDate(hire.hire_end_date)}
            </p>
          </div>
        </div>
        
        {/* Actions */}
        <div className="flex justify-between items-center pt-4 border-t border-gray-200">
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <div className="flex items-center gap-1">
              {hire.creator_profile_picture ? (
                <img
                  src={hire.creator_profile_picture}
                  alt="Creator"
                  className="w-5 h-5 rounded-full object-cover border border-gray-200"
                />
              ) : (
                <UserCircleIcon className="w-5 h-5 text-gray-400" />
              )}
              <span>
                Created by {hire.creator_name || 'Unknown'} on {formatDate(hire.created_at)}
              </span>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleEditHire(hire);
              }}
              className="text-blue-600 hover:text-blue-800 p-1"
              title="Edit hire"
            >
              <PencilIcon className="h-4 w-4" />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteHire(hire.id);
              }}
              className="text-red-600 hover:text-red-800 p-1"
              title="Delete hire"
            >
              <TrashIcon className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
  const [searchParams] = useSearchParams();
  const [hires, setHires] = useState<Hire[]>([]);
  const [equipment, setEquipment] = useState<Equipment[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [sites, setSites] = useState<Site[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [editingHire, setEditingHire] = useState<Hire | null>(null);
  const [showEquipmentModal, setShowEquipmentModal] = useState(false);
  const [showSupplierModal, setShowSupplierModal] = useState(false);
  const [showSiteModal, setShowSiteModal] = useState(false);
  const [exportingToZahara, setExportingToZahara] = useState<string | null>(null);
  const [zaharaSettings, setZaharaSettings] = useState<any>(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showPurchaseOrderModal, setShowPurchaseOrderModal] = useState(false);
  const [newHireId, setNewHireId] = useState<string | null>(null);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState<Supplier | null>(null);
  const [selectedHires, setSelectedHires] = useState<string[]>([]);
  const [sortBy, setSortBy] = useState<'expiry_date' | 'site' | 'none'>('none');

  // Debug email modal state
  useEffect(() => {
    console.log('🔍 Email modal state changed:', { showEmailModal, selectedSupplier: selectedSupplier?.name, newHireId, selectedHires: selectedHires.length });
  }, [showEmailModal, selectedSupplier, newHireId, selectedHires]);
  const [showBulkOrderModal, setShowBulkOrderModal] = useState(false);
  const [showBulkStatusModal, setShowBulkStatusModal] = useState(false);
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [subscriptionUsage, setSubscriptionUsage] = useState<{
    plan: string;
    limits: { hires: number; users: number };
    usage: {
      hires: { current: number; max: number };
      users: { current: number; max: number };
    };
  } | null>(null);
  const [bulkStatusAction, setBulkStatusAction] = useState<'completed' | 'collection_booked' | null>(null);
  const [showExtendModal, setShowExtendModal] = useState(false);
  const [showZaharaExtensionModal, setShowZaharaExtensionModal] = useState(false);
  const [extendDate, setExtendDate] = useState('');
  const [isExtendingHire, setIsExtendingHire] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [equipmentSearchTerm, setEquipmentSearchTerm] = useState('');
  const [showEquipmentDropdown, setShowEquipmentDropdown] = useState(false);
  const [showCompleted, setShowCompleted] = useState(false);
  
  // VAT rate constant
  const VAT_RATE = 0.20; // 20%
  const [formData, setFormData] = useState({
    equipment_id: '',
    supplier_id: '',
    site_id: '',
    price: '',
    pricing_term: 'day' as 'fixed' | 'day' | 'week' | 'month' | 'year',
    delivery_charge: '',
    operator_cost: '',
    hire_start_date: '',
    hire_end_date: '',
    hire_duration_days: '',
    total_cost: '',
    hire_status: 'active' as 'active' | 'completed' | 'collection_booked',
    net_cost: '',
    vat_amount: ''
  });
  
  // Equipment form data
  const [equipmentFormData, setEquipmentFormData] = useState({
    name: '',
    description: '',
    category: '',
    unit_of_measure: '',
    power: '',
    image: null as File | null
  });

  // Supplier form data
  const [supplierFormData, setSupplierFormData] = useState({
    name: '',
    contact_email: '',
    contact_phone: '',
    address: ''
  });

  // Site form data
  const [siteFormData, setSiteFormData] = useState({
    name: '',
    address_line_1: '',
    address_line_2: '',
    city: '',
    county: '',
    postcode: '',
    country: 'GB',
    job_number: '',
    status: 'active' as 'active' | 'inactive' | 'completed'
  });

  const searchInputRef = useRef<HTMLInputElement>(null);
  const debouncedSearchRef = useRef<number | null>(null);
  const equipmentDropdownRef = useRef<HTMLDivElement>(null);

  // Filter equipment based on search term
  const filteredEquipment = equipment.filter(eq => 
    eq.name.toLowerCase().includes(equipmentSearchTerm.toLowerCase()) ||
    eq.category.toLowerCase().includes(equipmentSearchTerm.toLowerCase())
  );

  // Handle click outside equipment dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (equipmentDropdownRef.current && !equipmentDropdownRef.current.contains(event.target as Node)) {
        setShowEquipmentDropdown(false);
      }
    };

    if (showEquipmentDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showEquipmentDropdown]);

  const fetchHires = useCallback(async (search = '', page = 1, showLoading = true) => {
    try {
      if (showLoading) setLoading(true);
      // Filter out completed hires unless showCompleted is true
      const statusFilter = showCompleted ? undefined : 'active,collection_booked';
      console.log('Fetching hires with status filter:', statusFilter, 'showCompleted:', showCompleted);
      const response = await hiresAPI.getAll(page, 12, search, statusFilter);
      console.log('🔍 Hires Loaded:', response.data.hires.map(h => ({
        id: h.id,
        equipment_name: h.equipment_name,
        zahara_document_id: h.zahara_document_id,
        zahara_po_number: h.zahara_po_number
      })));
      
      let sortedHires = response.data.hires;
      
      // Apply sorting
      if (sortBy === 'expiry_date') {
        sortedHires = [...response.data.hires].sort((a, b) => {
          const dateA = new Date(a.hire_end_date);
          const dateB = new Date(b.hire_end_date);
          return dateA.getTime() - dateB.getTime();
        });
      } else if (sortBy === 'site') {
        sortedHires = [...response.data.hires].sort((a, b) => {
          // Group by site name, then sort by expiry date within each site
          const siteCompare = a.site_name.localeCompare(b.site_name);
          if (siteCompare !== 0) return siteCompare;
          const dateA = new Date(a.hire_end_date);
          const dateB = new Date(b.hire_end_date);
          return dateA.getTime() - dateB.getTime();
        });
      }
      
      setHires(sortedHires);
      setTotalPages(response.data.pagination.totalPages);
      setCurrentPage(response.data.pagination.currentPage);
    } catch (error) {
      console.error('Error fetching hires:', error);
    } finally {
      if (showLoading) setLoading(false);
    }
  }, [showCompleted, sortBy]);

  // Handle URL parameters for pre-filling form from Equipment page
  useEffect(() => {
    const equipmentId = searchParams.get('equipment_id');
    const supplierId = searchParams.get('supplier_id');
    const pricePerDay = searchParams.get('price_per_day');
    const openModal = searchParams.get('open_modal');
    const editHireId = searchParams.get('edit');
    
    if (equipmentId) {
      // Pre-fill equipment
      setFormData(prev => ({ ...prev, equipment_id: equipmentId }));
      
      // Find equipment name for display
      const equipmentItem = equipment.find(eq => eq.id === equipmentId);
      if (equipmentItem) {
        setEquipmentSearchTerm(equipmentItem.name);
      }
      
      // Auto-open the modal
      setShowModal(true);
    }
    
    if (supplierId) {
      setFormData(prev => ({ ...prev, supplier_id: supplierId }));
    }
    
    if (pricePerDay) {
      setFormData(prev => ({ ...prev, price: pricePerDay }));
    }
    
    // Handle open_modal parameter from Dashboard
    if (openModal === 'true') {
      setShowModal(true);
    }
    
    // Handle edit hire parameter from Plant Reminders
    if (editHireId) {
      // Find the hire to edit
      const hireToEdit = hires.find(hire => hire.id === editHireId);
      if (hireToEdit) {
        handleEditHire(hireToEdit);
      }
    }
    
    // Clear URL parameters after processing
    if (equipmentId || supplierId || pricePerDay || openModal || editHireId) {
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.delete('equipment_id');
      newUrl.searchParams.delete('supplier_id');
      newUrl.searchParams.delete('price_per_day');
      newUrl.searchParams.delete('equipment_name');
      newUrl.searchParams.delete('open_modal');
      newUrl.searchParams.delete('edit');
      window.history.replaceState({}, '', newUrl.toString());
    }
  }, [searchParams, equipment, hires]);

  const fetchEquipment = async () => {
    try {
      const response = await fetch('/api/equipment?limit=1000', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();
      setEquipment(data.equipment || []);
    } catch (error) {
      console.error('Error fetching equipment:', error);
    }
  };

  const fetchSuppliers = async () => {
    try {
      const response = await suppliersAPI.getFavorites({ limit: 1000 });
      setSuppliers(response.data.suppliers || []);
    } catch (error) {
      console.error('Error fetching favorite suppliers:', error);
    }
  };

  const fetchSites = async () => {
    try {
      const response = await fetch('/api/sites?limit=1000', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();
      setSites(data.sites || []);
    } catch (error) {
      console.error('Error fetching sites:', error);
    }
  };

  // Check if Zahara is configured
  const checkZaharaSettings = async () => {
    try {
      const response = await zaharaAPI.getSettings();
      console.log('🔍 Zahara Settings Loaded:', response.data);
      setZaharaSettings(response.data);
    } catch (error) {
      console.log('🔍 Zahara not configured:', error);
      setZaharaSettings(null);
    }
  };

  useEffect(() => {
    fetchHires('', currentPage);
    fetchEquipment();
    fetchSuppliers();
    fetchSites();
    checkZaharaSettings();
  }, [currentPage, showCompleted, fetchHires]);

  // Refresh hire data when success modal closes (if needed)
  useEffect(() => {
    if (refreshTrigger > 0) {
      console.log('Refreshing hire data, trigger:', refreshTrigger);
      // Only refresh if we're not already updating the local state
      setTimeout(() => {
        fetchHires('', currentPage, false);
      }, 100);
    }
  }, [refreshTrigger, currentPage, fetchHires]);

  const calculateDuration = useCallback(() => {
    if (formData.hire_start_date && formData.hire_end_date) {
      const start = new Date(formData.hire_start_date);
      const end = new Date(formData.hire_end_date);
      const duration = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1;
      
      // Calculate total cost based on pricing term
      const price = parseFloat(formData.price || '0');
      const deliveryCost = parseFloat(formData.delivery_charge || '0');
      const operatorCost = parseFloat(formData.operator_cost || '0');
      let hireCost = 0;
      
      switch (formData.pricing_term) {
        case 'fixed':
          hireCost = price; // Fixed price regardless of duration
          break;
        case 'day':
          hireCost = price * duration;
          break;
        case 'week':
          hireCost = price * Math.ceil(duration / 7);
          break;
        case 'month':
          hireCost = price * Math.ceil(duration / 30);
          break;
        case 'year':
          hireCost = price * Math.ceil(duration / 365);
          break;
        default:
          hireCost = price * duration;
      }
      
      const netCost = hireCost + deliveryCost + operatorCost;
      const vatAmount = netCost * VAT_RATE;
      const totalCost = netCost * (1 + VAT_RATE);
      
      console.log('VAT Calculation Debug:', {
        hireCost,
        deliveryCost,
        operatorCost,
        netCost,
        vatAmount,
        totalCost,
        vatRate: VAT_RATE
      });
      
      setFormData(prev => ({
        ...prev,
        hire_duration_days: duration.toString(),
        net_cost: netCost.toFixed(2),
        vat_amount: vatAmount.toFixed(2),
        total_cost: totalCost.toFixed(2)
      }));
    }
  }, [formData.hire_start_date, formData.hire_end_date, formData.pricing_term, formData.price, formData.delivery_charge, formData.operator_cost]);

  // Auto-calculate VAT breakdown when relevant fields change
  useEffect(() => {
    if (formData.hire_start_date && formData.hire_end_date && formData.price) {
      calculateDuration();
    }
  }, [formData.hire_start_date, formData.hire_end_date, formData.pricing_term, formData.price, formData.delivery_charge, formData.operator_cost, calculateDuration]);

  // Export hire to Zahara as purchase order
  const handleExportToZahara = async (hireId: string) => {
    try {
      setExportingToZahara(hireId);
      const response = await zaharaAPI.createPurchaseOrder(hireId);
      
      // Update the hire in the local state
      setHires(prevHires => 
        prevHires.map(hire => 
          hire.id === hireId 
            ? { 
                ...hire, 
                zahara_document_id: response.data.zaharaPoId,
                zahara_po_number: response.data.zaharaPoNumber
              }
            : hire
        )
      );
      
      // Update the editing hire state as well (if it's the same hire)
      if (editingHire && editingHire.id === hireId) {
        setEditingHire(prev => prev ? {
          ...prev,
          zahara_document_id: response.data.zaharaPoId,
          zahara_po_number: response.data.zaharaPoNumber
        } : null);
      }
      
      // Show success modal
      setSuccessMessage(`Purchase Order #${response.data.zaharaPoNumber || response.data.zaharaPoId} successfully created in Zahara!`);
      setShowSuccessModal(true);
    } catch (error: any) {
      console.error('Error creating purchase order:', error);
      alert(`Failed to create purchase order: ${error.response?.data?.error || error.message}`);
    } finally {
      setExportingToZahara(null);
    }
  };

  const handlePurchaseOrderConfirm = async () => {
    if (!newHireId) return;
    
    try {
      await handleExportToZahara(newHireId);
    } finally {
      setShowPurchaseOrderModal(false);
      setNewHireId(null);
    }
  };

  const handlePurchaseOrderDecline = () => {
    setShowPurchaseOrderModal(false);
    setNewHireId(null);
    setSuccessMessage('Hire created successfully!');
    setShowSuccessModal(true);
  };

  // Multi-select functionality
  const handleHireSelect = (hireId: string) => {
    setSelectedHires(prev => 
      prev.includes(hireId) 
        ? prev.filter(id => id !== hireId)
        : [...prev, hireId]
    );
  };

  const handleSelectAll = () => {
    if (selectedHires.length === hires.length) {
      setSelectedHires([]);
    } else {
      setSelectedHires(hires.map(hire => hire.id));
    }
  };

  const handleBulkOrder = () => {
    if (selectedHires.length === 0) return;
    
    // Group selected hires by supplier
    const groupedHires = selectedHires.reduce((groups, hireId) => {
      const hire = hires.find(h => h.id === hireId);
      if (hire && hire.supplier_id) {
        const supplierId = hire.supplier_id;
        if (!groups[supplierId]) {
          groups[supplierId] = [];
        }
        groups[supplierId].push(hire);
      }
      return groups;
    }, {} as Record<string, Hire[]>);

    const supplierCount = Object.keys(groupedHires).length;
    
    if (supplierCount > 1) {
      alert('Selected hires must be from the same supplier. Please select hires from one supplier only.');
      return;
    }

    setShowBulkOrderModal(true);
  };

  const handleBulkOrderConfirm = async () => {
    if (selectedHires.length === 0) return;
    
    try {
      // Create a single bulk purchase order for all selected hires
      const response = await zaharaAPI.createBulkPurchaseOrder(selectedHires);
      
      // Update the local state with the PO information for all selected hires
      setHires(prevHires => 
        prevHires.map(hire => 
          selectedHires.includes(hire.id) 
            ? { 
                ...hire, 
                zahara_document_id: response.data.zaharaPoId,
                zahara_po_number: response.data.zaharaPoNumber
              }
            : hire
        )
      );
      
      setShowBulkOrderModal(false);
      setSelectedHires([]);
      setSuccessMessage(`Successfully created bulk purchase order #${response.data.zaharaPoNumber || response.data.zaharaPoId} with ${response.data.lineItemCount} line items for ${response.data.hireCount} hire(s)!`);
      setShowSuccessModal(true);
    } catch (error: any) {
      console.error('Error creating bulk purchase order:', error);
      alert(`Failed to create bulk purchase order: ${error.response?.data?.error || error.message}`);
    }
  };

  const handleBulkStatus = (status: 'completed' | 'collection_booked') => {
    if (selectedHires.length === 0) return;
    setBulkStatusAction(status);
    setShowBulkStatusModal(true);
  };

  const handleBulkExtend = () => {
    if (selectedHires.length === 0) return;
    setShowExtendModal(true);
  };

  const handleExtendConfirm = async () => {
    if (selectedHires.length === 0 || !extendDate) return;
    
    // Check if any selected hires have Zahara integration
    const hiresWithZahara = hires.filter(hire => 
      selectedHires.includes(hire.id) && hire.zahara_document_id
    );
    
    console.log('🔍 Zahara Extension Check:', {
      selectedHires: selectedHires,
      zaharaSettings: {
        is_active: zaharaSettings?.is_active,
        has_api_key: !!zaharaSettings?.api_key
      },
      hiresWithZahara: hiresWithZahara.map(h => ({
        id: h.id,
        equipment_name: h.equipment_name,
        zahara_document_id: h.zahara_document_id,
        zahara_po_number: h.zahara_po_number
      })),
      willShowZaharaModal: !!(zaharaSettings && zaharaSettings.is_active && zaharaSettings.api_key && hiresWithZahara.length > 0)
    });
    
    if (zaharaSettings && zaharaSettings.is_active && zaharaSettings.api_key && hiresWithZahara.length > 0) {
      // Show Zahara extension choice modal
      console.log('🎯 Showing Zahara extension modal');
      setShowZaharaExtensionModal(true);
    } else {
      // Proceed with regular extension (no Zahara)
      console.log('🎯 Proceeding with regular extension (no Zahara)');
      await performHireExtension('none');
    }
  };

  const performHireExtension = async (zaharaAction: 'none' | 'new_order' | 'update_existing') => {
    try {
      setIsExtendingHire(true);
      // Calculate extension data upfront
      const extensionData = selectedHires.map(hireId => {
        const hire = hires.find(h => h.id === hireId);
        if (!hire) return null;

        const originalEndDate = new Date(hire.hire_end_date);
        const newEndDate = new Date(extendDate);
        
        // Calculate extension days
        const extensionDays = Math.ceil((newEndDate.getTime() - originalEndDate.getTime()) / (1000 * 60 * 60 * 24));
        
        // Calculate extension cost
        const pricePerDay = parseFloat(String(hire.price_per_day || hire.price || '0'));
        const extensionCost = extensionDays * pricePerDay;

        return {
          hireId: hire.id,
          oldEndDate: originalEndDate.toISOString(),
          newEndDate: extendDate,
          extensionDays: extensionDays,
          pricePerDay: pricePerDay.toString(),
          equipmentName: hire.equipment_name,
          siteName: hire.site_name,
          extensionCost: extensionCost
        };
      }).filter(Boolean);

      console.log('🔍 Frontend Extension Data:', {
        hireIds: selectedHires,
        newEndDate: extendDate,
        zaharaAction: zaharaAction,
        extensionData: extensionData
      });

      const response = await hiresAPI.bulkExtend(selectedHires, extendDate, zaharaAction);
      
      // Update the local state with the new end dates and total costs
      setHires(prevHires => 
        prevHires.map(hire => 
          selectedHires.includes(hire.id) 
            ? { 
                ...hire, 
                hire_end_date: extendDate,
                hire_duration_days: response.data.extendedHires.find((h: any) => h.id === hire.id)?.hire_duration_days || hire.hire_duration_days,
                total_cost: response.data.extendedHires.find((h: any) => h.id === hire.id)?.total_cost || hire.total_cost
              }
            : hire
        )
      );
      
      setShowExtendModal(false);
      setShowZaharaExtensionModal(false);
      setSelectedHires([]);
      setExtendDate('');
      
      let successMessage = `Successfully extended ${response.data.extendedCount} hire(s) to ${new Date(extendDate).toLocaleDateString('en-GB')}!`;
      if (zaharaAction === 'new_order') {
        successMessage += ` New purchase order created.`;
      } else if (zaharaAction === 'update_existing') {
        successMessage += ` Existing purchase order updated.`;
      }
      
      setSuccessMessage(successMessage);
      setShowSuccessModal(true);
    } catch (error: any) {
      console.error('Error extending hires:', error);
      alert(`Failed to extend hires: ${error.response?.data?.error || error.message}`);
    } finally {
      setIsExtendingHire(false);
    }
  };

  const handleBulkStatusConfirm = async () => {
    if (selectedHires.length === 0 || !bulkStatusAction) return;
    
    console.log('Bulk status update - Selected hires:', selectedHires);
    console.log('Bulk status update - Status:', bulkStatusAction);
    
    try {
      const response = await hiresAPI.bulkUpdateStatus(selectedHires, bulkStatusAction);
      
      console.log('Bulk status update response:', response.data);
      
      // Update the local state with the new status
      setHires(prevHires => 
        prevHires.map(hire => 
          selectedHires.includes(hire.id) 
            ? { ...hire, hire_status: bulkStatusAction }
            : hire
        )
      );
      
      setShowBulkStatusModal(false);
      setSelectedHires([]);
      setBulkStatusAction(null);
      setSuccessMessage(`Successfully updated ${response.data.updatedCount} hire(s) to ${formatStatusDisplay(bulkStatusAction)}!`);
      setShowSuccessModal(true);
    } catch (error: any) {
      console.error('Error bulk updating status:', error);
      console.error('Error response:', error.response?.data);
      alert(`Failed to update status: ${error.response?.data?.error || error.message}`);
    }
  };

  const handleBulkBookingEmail = () => {
    if (selectedHires.length === 0) return;
    
    console.log('🔍 Bulk booking email triggered with selected hires:', selectedHires);
    
    // Check if all selected hires have the same supplier with email
    const selectedHireData = selectedHires.map(hireId => 
      hires.find(h => h.id === hireId)
    ).filter(Boolean);
    
    console.log('🔍 Selected hire data:', selectedHireData);
    
    if (selectedHireData.length === 0) {
      alert('No valid hires selected');
      return;
    }
    
    // Check if all hires have the same supplier
    const supplierIds = [...new Set(selectedHireData.map(hire => hire?.supplier_id).filter(Boolean))];
    console.log('🔍 Supplier IDs:', supplierIds);
    
    if (supplierIds.length > 1) {
      alert('Please select hires from the same supplier for booking email');
      return;
    }
    
    const firstHire = selectedHireData[0];
    if (!firstHire) {
      alert('No valid hire found');
      return;
    }
    
    const supplier = suppliers.find(s => s.id === firstHire.supplier_id);
    
    console.log('🔍 Found supplier:', supplier);
    
    if (!supplier || !supplier.contact_email) {
      alert('Selected supplier does not have an email address');
      return;
    }
    
    // Set the supplier and show the email modal
    console.log('🔍 Setting supplier and showing email modal');
    setSelectedSupplier(supplier);
    setShowEmailModal(true);
  };

  const handleBulkDelete = () => {
    if (selectedHires.length === 0) return;
    setShowBulkDeleteModal(true);
  };

  const handleBulkDeleteConfirm = async () => {
    if (selectedHires.length === 0) return;
    
    try {
      const response = await hiresAPI.bulkDelete(selectedHires);
      
      // Remove the deleted hires from local state
      setHires(prevHires => 
        prevHires.filter(hire => !selectedHires.includes(hire.id))
      );
      
      setShowBulkDeleteModal(false);
      setSelectedHires([]);
      setSuccessMessage(`Successfully deleted ${response.data.deletedCount} hire(s)!`);
      setShowSuccessModal(true);
    } catch (error: any) {
      console.error('Error bulk deleting hires:', error);
      alert(`Failed to delete hires: ${error.response?.data?.error || error.message}`);
    }
  };

  const handleSearchChange = () => {
    const value = searchInputRef.current?.value || '';
    
    if (debouncedSearchRef.current) {
      clearTimeout(debouncedSearchRef.current);
    }
    
    debouncedSearchRef.current = window.setTimeout(() => {
      fetchHires(value, 1, false);
    }, 300);
  };

  useEffect(() => {
    return () => {
      if (debouncedSearchRef.current) {
        clearTimeout(debouncedSearchRef.current);
      }
    };
  }, []);

  const resetForm = () => {
    setFormData({
      equipment_id: '',
      supplier_id: '',
      site_id: '',
      price: '',
      pricing_term: 'day' as 'fixed' | 'day' | 'week' | 'month' | 'year',
      delivery_charge: '',
      operator_cost: '',
      hire_start_date: '',
      hire_end_date: '',
      hire_duration_days: '',
      total_cost: '',
      hire_status: 'active' as 'active' | 'completed' | 'collection_booked',
      net_cost: '',
      vat_amount: ''
    });
    setEditingHire(null);
    setEquipmentSearchTerm('');
    setShowEquipmentDropdown(false);
  };

  const handleAddHire = async () => {
    try {
      // Check subscription limits before showing modal
      const response = await hiresAPI.getSubscriptionUsage();
      const usage = response.data;
      
      console.log('Frontend subscription check:', usage);
      
      if (usage.usage.hires.current >= usage.usage.hires.max) {
        // Show upgrade modal instead of alert
        setSubscriptionUsage(usage);
        setShowUpgradeModal(true);
        return;
      }
      
      // If within limits, show the modal
      resetForm();
      setShowModal(true);
    } catch (error) {
      console.error('Error checking subscription limits:', error);
      // If there's an error checking limits, still show the modal (fail open)
      resetForm();
      setShowModal(true);
    }
  };
  
  const resetEquipmentForm = () => {
    setEquipmentFormData({
      name: '',
      description: '',
      category: '',
      unit_of_measure: '',
      power: '',
      image: null
    });
  };

  const resetSupplierForm = () => {
    setSupplierFormData({
      name: '',
      contact_email: '',
      contact_phone: '',
      address: ''
    });
  };

  const resetSiteForm = () => {
    setSiteFormData({
      name: '',
      address_line_1: '',
      address_line_2: '',
      city: '',
      county: '',
      postcode: '',
      country: 'GB',
      job_number: '',
      status: 'active'
    });
  };
  
  const handleAddEquipment = () => {
    resetEquipmentForm();
    setShowEquipmentModal(true);
  };

  const handleEquipmentSelect = (equipmentId: string, equipmentName: string) => {
    setFormData(prev => ({ ...prev, equipment_id: equipmentId }));
    setEquipmentSearchTerm(equipmentName);
    setShowEquipmentDropdown(false);
  };

  const handleAddSupplier = () => {
    resetSupplierForm();
    setShowSupplierModal(true);
  };

  const handleAddSite = () => {
    resetSiteForm();
    setShowSiteModal(true);
  };
  
  const handleSubmitEquipment = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const response = await equipmentAPI.create({
        name: equipmentFormData.name,
        description: equipmentFormData.description,
        category: equipmentFormData.category,
        unit_of_measure: equipmentFormData.unit_of_measure,
        power: equipmentFormData.power,
        image: equipmentFormData.image || undefined
      });
      await fetchEquipment();
      setFormData(prev => ({ ...prev, equipment_id: response.data.equipment.id }));
      setShowEquipmentModal(false);
    } catch (error) {
      console.error('Error creating equipment:', error);
    }
  };

  const handleSubmitSupplier = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const response = await suppliersAPI.create({
        name: supplierFormData.name,
        contact_email: supplierFormData.contact_email,
        contact_phone: supplierFormData.contact_phone,
        address: supplierFormData.address
      });
      await fetchSuppliers();
      setFormData(prev => ({ ...prev, supplier_id: response.data.supplier.id }));
      setShowSupplierModal(false);
    } catch (error) {
      console.error('Error creating supplier:', error);
    }
  };

  const handleSubmitSite = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const response = await sitesAPI.create({
        name: siteFormData.name,
        address_line_1: siteFormData.address_line_1,
        address_line_2: siteFormData.address_line_2,
        city: siteFormData.city,
        county: siteFormData.county,
        postcode: siteFormData.postcode,
        country: siteFormData.country,
        job_number: siteFormData.job_number,
        status: siteFormData.status
      });
      await fetchSites();
      setFormData(prev => ({ ...prev, site_id: response.data.site.id }));
      setShowSiteModal(false);
    } catch (error) {
      console.error('Error creating site:', error);
    }
  };

  const handleEditHire = (hire: Hire) => {
    // Format dates for HTML date input (YYYY-MM-DD)
    const formatDateForInput = (dateString: string) => {
      const date = new Date(dateString);
      return date.toISOString().split('T')[0];
    };

    // Calculate net cost and VAT for editing
    const netCost = hire.total_cost / (1 + VAT_RATE);
    const vatAmount = netCost * VAT_RATE;
    
    setFormData({
      equipment_id: hire.equipment_id || '',
      supplier_id: hire.supplier_id || '',
      site_id: hire.site_id || '',
      price: hire.price.toString(),
      pricing_term: hire.pricing_term || 'day',
      delivery_charge: hire.delivery_charge?.toString() || '0',
      operator_cost: hire.operator_cost?.toString() || '0',
      hire_start_date: formatDateForInput(hire.hire_start_date),
      hire_end_date: formatDateForInput(hire.hire_end_date),
      hire_duration_days: hire.hire_duration_days.toString(),
      total_cost: hire.total_cost.toString(),
      hire_status: hire.hire_status as 'active' | 'completed' | 'collection_booked',
      net_cost: netCost.toFixed(2),
      vat_amount: vatAmount.toFixed(2)
    });
    setEditingHire(hire);
    setEquipmentSearchTerm(hire.equipment_name);
    setShowModal(true);
  };


  const handleDeleteHire = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this hire?')) return;

    try {
      await hiresAPI.delete(id);
      fetchHires('', currentPage);
    } catch (error) {
      console.error('Error deleting hire:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const hireData = {
        ...formData,
        price: parseFloat(formData.price),
        pricing_term: formData.pricing_term,
        hire_duration_days: parseInt(formData.hire_duration_days),
        total_cost: parseFloat(formData.total_cost),
        delivery_charge: formData.delivery_charge === '' ? 0 : parseFloat(formData.delivery_charge),
        operator_cost: formData.operator_cost === '' ? 0 : parseFloat(formData.operator_cost)
      };

      if (editingHire) {
        await hiresAPI.update(editingHire.id, hireData);
        setShowModal(false);
        resetForm();
        fetchHires('', currentPage);
      } else {
        // For new hires, check if Zahara is configured
        const response = await hiresAPI.create(hireData);
        const createdHireId = response.data.hire.id;
        
        setShowModal(false);
        resetForm();
        fetchHires('', currentPage);
        
        // Check if supplier has email address
        const selectedSupplierData = suppliers.find(s => s.id === formData.supplier_id);
        
        // Show purchase order confirmation if Zahara is configured
        if (zaharaSettings && zaharaSettings.is_active && zaharaSettings.api_key) {
          setNewHireId(createdHireId);
          setSelectedSupplier(selectedSupplierData || null);
          setShowPurchaseOrderModal(true);
        } else if (selectedSupplierData && selectedSupplierData.contact_email) {
          // Show email option if supplier has email but no Zahara
          setNewHireId(createdHireId);
          setSelectedSupplier(selectedSupplierData);
          setShowEmailModal(true);
        } else {
          // Show regular success message if no Zahara integration and no supplier email
          setSuccessMessage('Hire created successfully!');
          setShowSuccessModal(true);
        }
      }
    } catch (error) {
      console.error('Error saving hire:', error);
    }
  };

  const calculatePricePerDay = (price: number, term: string, durationDays: number) => {
    if (!price || !durationDays) return 0;
    
    switch (term) {
      case 'fixed':
        return price / durationDays;
      case 'day':
        return price;
      case 'week':
        return price / 7;
      case 'month':
        return price / 30; // Approximate
      case 'year':
        return price / 365; // Approximate
      default:
        return price;
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP'
    }).format(amount);
  };

  const formatPricePerDay = (pricePerDay: number | string | undefined) => {
    if (!pricePerDay) return '';
    const num = typeof pricePerDay === 'string' ? parseFloat(pricePerDay) : pricePerDay;
    return isNaN(num) ? '' : num.toFixed(2);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-GB');
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'completed': return 'bg-blue-100 text-blue-800';
      case 'collection_booked': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatStatusDisplay = (status: string) => {
    switch (status) {
      case 'active': return 'Active';
      case 'completed': return 'Completed';
      case 'collection_booked': return 'Collection Booked';
      default: return status;
    }
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center">
          <WrenchScrewdriverIcon className="h-8 w-8 text-blue-600 mr-3" />
          <h1 className="text-3xl font-bold text-gray-900">{t('hires.title')}</h1>
        </div>
        <div className="flex items-center gap-3">
          {/* View Toggle Buttons */}

          
          <button
            onClick={handleAddHire}
            className="btn btn-primary"
          >
            <PlusIcon className="h-5 w-5" />
{t('hires.addHire')}
          </button>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="mb-6">
        <div className="flex gap-4 items-center flex-wrap">
          <div className="relative flex-1 min-w-64">
            <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              ref={searchInputRef}
              type="text"
              placeholder={t('hires.searchPlaceholder')}
              onChange={handleSearchChange}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          
          {/* Sort Dropdown */}
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-700 whitespace-nowrap">
              Sort by:
            </label>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as 'expiry_date' | 'site' | 'none')}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
            >
              <option value="none">Default</option>
              <option value="expiry_date">Expiry Date</option>
              <option value="site">Site (Grouped)</option>
            </select>
          </div>
          
          {/* Show Completed Toggle */}
          <div className="flex items-center">
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={showCompleted}
                onChange={(e) => setShowCompleted(e.target.checked)}
                className="sr-only"
              />
              <div className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                showCompleted ? 'bg-blue-600' : 'bg-gray-200'
              }`}>
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  showCompleted ? 'translate-x-6' : 'translate-x-1'
                }`} />
              </div>
              <span className="ml-3 text-sm font-medium text-gray-700">
                Show Completed
              </span>
            </label>
          </div>
        </div>
      </div>

      {/* Hires List */}
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      ) : (
        <>
          {hires.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500 text-lg">
{searchInputRef.current?.value ? t('hires.noHiresMatchingSearch') : t('hires.noHiresFound')}
              </p>
            </div>
          ) : (
            <>
              {/* Bulk Actions Header */}
              {hires.length > 0 && (
                <div className="bg-blue-50 dark:bg-blue-900 border border-blue-200 dark:border-blue-700 rounded-lg p-4 mb-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedHires.length === hires.length && hires.length > 0}
                          onChange={handleSelectAll}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          {t('hires.selectAll')} ({hires.length} {t('navigation.hires')})
                        </span>
                      </label>
                      {selectedHires.length > 0 && (
                        <span className="text-sm text-blue-600 dark:text-blue-400 font-medium">
                          {selectedHires.length} {t('hires.selected')}
                        </span>
                      )}
                    </div>
                    {selectedHires.length > 0 && (
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setSelectedHires([])}
                          className="px-3 py-1 text-sm text-gray-600 hover:text-gray-800"
                        >
{t('common.clear')}
                        </button>
                        
                        {/* Actions Dropdown */}
                        <div className="dropdown dropdown-end">
                          <div tabIndex={0} role="button" className="btn btn-secondary btn-sm flex items-center gap-2">
                            Actions
                            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          </div>
                          <ul tabIndex={0} className="dropdown-content menu bg-base-100 rounded-box z-[1] w-52 p-2 shadow-lg border">
                            <li>
                              <button
                                onClick={() => handleBulkStatus('completed')}
                                className="flex items-center gap-2 text-green-700 hover:bg-green-50"
                              >
                                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                                {formatStatusDisplay('completed')}
                              </button>
                            </li>
                            <li>
                              <button
                                onClick={() => handleBulkStatus('collection_booked')}
                                className="flex items-center gap-2 text-orange-700 hover:bg-orange-50"
                              >
                                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                                {formatStatusDisplay('collection_booked')}
                              </button>
                            </li>
                            <li>
                              <button
                                onClick={handleBulkExtend}
                                className="flex items-center gap-2 text-purple-700 hover:bg-purple-50"
                              >
                                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                Extend Hire
                              </button>
                            </li>
                            {zaharaSettings && zaharaSettings.is_active && zaharaSettings.api_key && (
                              <li>
                                <button
                                  onClick={handleBulkOrder}
                                  className="flex items-center gap-2 text-blue-700 hover:bg-blue-50"
                                >
                                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                  </svg>
                                  {t('hires.createOrder')} ({selectedHires.length})
                                </button>
                              </li>
                            )}
                            <li>
                              <button
                                onClick={handleBulkBookingEmail}
                                className="flex items-center gap-2 text-indigo-700 hover:bg-indigo-50"
                              >
                                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                </svg>
                                Booking Email
                              </button>
                            </li>
                            <li>
                              <button
                                onClick={handleBulkDelete}
                                className="flex items-center gap-2 text-red-700 hover:bg-red-50"
                              >
                                <TrashIcon className="h-4 w-4" />
                                {t('common.delete')}
                              </button>
                            </li>
                          </ul>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              <div className="space-y-6">
              {sortBy === 'site' ? (
                // Group by site
                (() => {
                  const groupedHires = hires.reduce((acc, hire) => {
                    const siteName = hire.site_name;
                    if (!acc[siteName]) {
                      acc[siteName] = [];
                    }
                    acc[siteName].push(hire);
                    return acc;
                  }, {} as Record<string, Hire[]>);

                  return Object.entries(groupedHires).map(([siteName, siteHires]) => (
                    <div key={siteName} className="space-y-4">
                      {/* Site Header */}
                      <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 border-l-4 border-blue-500">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                          <MapPinIcon className="h-5 w-5 text-blue-500" />
                          {siteName}
                        </h3>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          {siteHires.length} equipment hire{siteHires.length !== 1 ? 's' : ''}
                        </p>
                      </div>
                      
                      {/* Hires for this site */}
                      <div className="space-y-4 ml-4">
                        {siteHires.map((hire) => (
                          <div 
                            key={hire.id} 
                            className={`bg-white dark:bg-gray-800 rounded-lg shadow-md dark:shadow-gray-900 border p-6 hover:shadow-lg dark:hover:shadow-gray-800 transition-all ${
                              selectedHires.includes(hire.id) 
                                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900' 
                                : 'border-gray-200 dark:border-gray-700'
                            }`}
                          >
                            {renderHireCard(hire)}
                          </div>
                        ))}
                      </div>
                    </div>
                  ));
                })()
              ) : (
                // Default rendering without grouping
                hires.map((hire) => (
                  <div 
                    key={hire.id} 
                    className={`bg-white dark:bg-gray-800 rounded-lg shadow-md dark:shadow-gray-900 border p-6 hover:shadow-lg dark:hover:shadow-gray-800 transition-all ${
                      selectedHires.includes(hire.id) 
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900' 
                        : 'border-gray-200 dark:border-gray-700'
                    }`}
                  >
                    {renderHireCard(hire)}
                  </div>
                ))
              )}
               
              </div>
            </>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex justify-center mt-8">
              <div className="flex gap-2">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <span className="px-3 py-2 text-sm text-gray-700">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl max-w-6xl w-full max-h-[95vh] overflow-hidden shadow-2xl dark:shadow-gray-900">
            {/* Header */}
            <div className="px-8 py-6">
              <h2 className="text-2xl font-bold">
{editingHire ? t('hires.editHire') : t('hires.addHire')}
              </h2>
              <p className="tmt-1">
                {editingHire ? 'Update hire details and pricing' : 'Create a new equipment hire'}
              </p>
            </div>
            
            {/* Form Content */}
            <div className="p-8 overflow-y-auto max-h-[calc(95vh-120px)]">
              <form onSubmit={handleSubmit} className="space-y-8">
                {/* Main Form Grid */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  {/* Equipment */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <label className="label">
                        Equipment *
                      </label>
                      <button
                        type="button"
                        onClick={handleAddEquipment}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors border border-blue-200"
                        title="Add new equipment"
                      >
                        <PlusIcon className="h-3.5 w-3.5" />
                        Add Equipment
                      </button>
                    </div>
                    
                    {/* Equipment Search and Selection */}
                    <div className="relative" ref={equipmentDropdownRef}>
                      <input
                        type="text"
                        placeholder="Search equipment..."
                        value={equipmentSearchTerm}
                        onChange={(e) => setEquipmentSearchTerm(e.target.value)}
                        onFocus={() => setShowEquipmentDropdown(true)}
                        className="w-full input"
                      />
                      <MagnifyingGlassIcon className="absolute right-3 top-2.5 h-4 w-4 text-gray-400" />
                      
                      {/* Equipment Dropdown */}
                      {showEquipmentDropdown && (
                        <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg shadow-lg dark:shadow-gray-900 max-h-60 overflow-y-auto">
                        {filteredEquipment.slice(0, 10).map((eq) => (
                          <div
                            key={eq.id}
                            onClick={() => handleEquipmentSelect(eq.id, eq.name)}
                            className={`px-3 py-2 cursor-pointer hover:bg-gray-50 ${
                              formData.equipment_id === eq.id ? 'bg-blue-50 text-blue-600' : ''
                            }`}
                          >
                            <div className="font-medium">{eq.name}</div>
                            <div className="text-sm text-gray-500">{eq.category}</div>
                          </div>
                        ))}
                        
                        {filteredEquipment.length > 10 && (
                          <div className="px-3 py-2 text-sm text-gray-500 border-t border-gray-200">
                            Showing 10 of {filteredEquipment.length} equipment items
                          </div>
                        )}
                        
                        {filteredEquipment.length === 0 && (
                          <div className="px-3 py-2 text-sm text-gray-500">
                            No equipment found
                          </div>
                        )}
                        </div>
                      )}
                    </div>
                    
                    {/* Selected Equipment Display */}
                    {formData.equipment_id && (
                      <div className="mt-2 p-2 bg-blue-50 rounded-lg">
                        <div className="text-sm font-medium text-blue-800">
                          Selected: {equipment.find(eq => eq.id === formData.equipment_id)?.name}
                        </div>
                        <div className="text-xs text-blue-600">
                          {equipment.find(eq => eq.id === formData.equipment_id)?.category}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Supplier */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <label className="label">
                        Supplier (Favorites) *
                      </label>
                      <button
                        type="button"
                        onClick={handleAddSupplier}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors border border-blue-200"
                        title="Add new supplier"
                      >
                        <PlusIcon className="h-3.5 w-3.5" />
                        Add Supplier
                      </button>
                    </div>
                    <select
                      value={formData.supplier_id}
                      onChange={(e) => setFormData(prev => ({ ...prev, supplier_id: e.target.value }))}
                      className="w-full select"
                      required
                    >
                      <option value="">Select Favorite Supplier</option>
                      {suppliers.map((supplier) => (
                        <option key={supplier.id} value={supplier.id}>
                          {supplier.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Site */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <label className="label">
                        Site *
                      </label>
                      <button
                        type="button"
                        onClick={handleAddSite}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors border border-blue-200"
                        title="Add new site"
                      >
                        <PlusIcon className="h-3.5 w-3.5" />
                        Add Site
                      </button>
                    </div>
                    <select
                      value={formData.site_id}
                      onChange={(e) => setFormData(prev => ({ ...prev, site_id: e.target.value }))}
                      className="w-full select"
                      required
                    >
                      <option value="">Select Site</option>
                      {sites.map((site) => (
                        <option key={site.id} value={site.id}>
                          {site.name} {site.job_number && `(${site.job_number})`}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Pricing Section */}
                  <div className="lg:col-span-2">
                    <h3 className="text-lg font-semibold text-gray-800 mb-4 pb-2 border-b border-gray-200">Pricing Details</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* Price */}
                      <div className="space-y-2">
                        <label className="label">
                          Price (£) *
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          value={formData.price}
                          onChange={(e) => {
                            setFormData(prev => ({ ...prev, price: e.target.value }));
                          }}
                          className="w-full input"
                          required
                        />
                      </div>

                      {/* Pricing Term */}
                      <div className="space-y-2">
                        <label className="label">
                          Pricing Term *
                        </label>
                        <select
                          value={formData.pricing_term}
                          onChange={(e) => {
                            setFormData(prev => ({ ...prev, pricing_term: e.target.value as 'fixed' | 'day' | 'week' | 'month' | 'year' }));
                          }}
                          className="w-full select"
                          required
                        >
                          <option value="fixed">Fixed Price</option>
                          <option value="day">Per Day</option>
                          <option value="week">Per Week</option>
                          <option value="month">Per Month</option>
                          <option value="year">Per Year</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Date Section */}
                  <div className="lg:col-span-2">
                    <h3 className="text-lg font-semibold text-gray-800 mb-4 pb-2 border-b border-gray-200">Hire Period</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* Start Date */}
                      <div className="space-y-2">
                        <label className="label">
                          Start Date *
                        </label>
                        <input
                          type="date"
                          value={formData.hire_start_date}
                          onChange={(e) => {
                            setFormData(prev => ({ ...prev, hire_start_date: e.target.value }));
                          }}
                          className="w-full input"
                          required
                        />
                      </div>

                      {/* End Date */}
                      <div className="space-y-2">
                        <label className="label">
                          End Date *
                        </label>
                        <input
                          type="date"
                          value={formData.hire_end_date}
                          onChange={(e) => {
                            setFormData(prev => ({ ...prev, hire_end_date: e.target.value }));
                          }}
                          className="w-full input"
                          required
                        />
                      </div>
                    </div>
                  </div>

                  {/* Additional Details Section */}
                  <div className="lg:col-span-2">
                    <h3 className="text-lg font-semibold text-gray-800 mb-4 pb-2 border-b border-gray-200">Additional Details</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* Delivery Charge */}
                      <div className="space-y-2">
                        <label className="label">
                          Delivery Charge (£)
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          value={formData.delivery_charge}
                          onChange={(e) => {
                            setFormData(prev => ({ ...prev, delivery_charge: e.target.value }));
                          }}
                          className="w-full input"
                          placeholder="0.00"
                        />
                      </div>

                      {/* Operator Cost */}
                      <div className="space-y-2">
                        <label className="label">
                          Operator Cost (£)
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          value={formData.operator_cost}
                          onChange={(e) => {
                            setFormData(prev => ({ ...prev, operator_cost: e.target.value }));
                          }}
                          className="w-full input"
                          placeholder="0.00"
                        />
                        <p className="text-xs text-gray-500">Cost for equipment operator (if applicable)</p>
                      </div>

                      {/* Status */}
                      <div className="space-y-2">
                        <label className="label">
                          Status
                        </label>
                        
                        {/* Status Suggestion Banner */}
                        {editingHire && (() => {
                          const endDate = new Date(editingHire.hire_end_date);
                          const today = new Date();
                          const daysUntilExpiry = Math.ceil((endDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
                          
                          // Show banner if within 5 days of expiry (including overdue)
                          if (daysUntilExpiry <= 5) {
                            const isOverdue = daysUntilExpiry < 0;
                            const bannerColor = isOverdue ? 'red' : 'orange';
                            const bannerBg = isOverdue ? 'bg-red-50 border-red-200' : 'bg-orange-50 border-orange-200';
                            const bannerText = isOverdue ? 'text-red-800' : 'text-orange-800';
                            const bannerSubtext = isOverdue ? 'text-red-700' : 'text-orange-700';
                            const iconColor = isOverdue ? 'text-red-600' : 'text-orange-600';
                            
                            return (
                              <div className={`${bannerBg} border rounded-lg p-3 mb-3`}>
                                <div className="flex items-start gap-2">
                                  <ExclamationTriangleIcon className={`h-5 w-5 ${iconColor} mt-0.5 flex-shrink-0`} />
                                  <div className="flex-1">
                                    <h4 className={`text-sm font-medium ${bannerText}`}>
                                      {isOverdue ? 'Hire is Overdue' : 'Hire Expires Soon'}
                                    </h4>
                                    <p className={`text-sm ${bannerSubtext} mt-1`}>
                                      {isOverdue 
                                        ? `This hire is ${Math.abs(daysUntilExpiry)} day${Math.abs(daysUntilExpiry) !== 1 ? 's' : ''} overdue. Please update the status.`
                                        : `This hire expires in ${daysUntilExpiry} day${daysUntilExpiry !== 1 ? 's' : ''}. Consider updating the status.`
                                      }
                                    </p>
                                    <div className="flex gap-2 mt-2">
                                      {editingHire.hire_status !== 'collection_booked' && (
                                        <button
                                          type="button"
                                          onClick={() => setFormData(prev => ({ ...prev, hire_status: 'collection_booked' }))}
                                          className="px-3 py-1 text-xs font-medium text-orange-700 bg-orange-100 rounded-md hover:bg-orange-200 transition-colors"
                                        >
                                          Mark as Collection Booked
                                        </button>
                                      )}
                                      {editingHire.hire_status !== 'completed' && (
                                        <button
                                          type="button"
                                          onClick={() => setFormData(prev => ({ ...prev, hire_status: 'completed' }))}
                                          className="px-3 py-1 text-xs font-medium text-green-700 bg-green-100 rounded-md hover:bg-green-200 transition-colors"
                                        >
                                          Mark as Completed
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          }
                          return null;
                        })()}
                        
                        <select
                          value={formData.hire_status}
                          onChange={(e) => setFormData(prev => ({ ...prev, hire_status: e.target.value as 'active' | 'completed' | 'collection_booked' }))}
                          className="w-full select"
                        >
                          <option value="active">Active</option>
                          <option value="completed">Completed</option>
                          <option value="collection_booked">Collection Booked</option>
                        </select>
                      </div>
                    </div>
                  </div>

                        {/* Summary Section */}
                        <div className="lg:col-span-2">
                          <h3 className="text-lg font-semibold text-gray-800 mb-4 pb-2 border-b border-gray-200">Hire Summary</h3>
                          <div className="card">
                            <div className="card-body">
                            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                              {/* Duration */}
                              <div className="space-y-2">
                                <label className="label">
                                  <CalculatorIcon className="h-4 w-4 text-blue-600" />
                                  Duration (days)
                                </label>
                                <input
                                  type="number"
                                  value={formData.hire_duration_days}
                                  onChange={(e) => setFormData(prev => ({ ...prev, hire_duration_days: e.target.value }))}
                                  className="w-full input"
                                  readOnly
                                />
                              </div>

                              {/* Net Cost */}
                              <div className="space-y-2">
                                <label className="label">
                                  <CalculatorIcon className="h-4 w-4 text-blue-600" />
                                  Net Cost (£)
                                </label>
                                <input
                                  type="number"
                                  step="0.01"
                                  value={formData.net_cost || ''}
                                  className="w-full input"
                                  readOnly
                                />
                              </div>

                              {/* VAT */}
                              <div className="space-y-2">
                                <label className="label">
                                  <CalculatorIcon className="h-4 w-4 text-orange-600" />
                                  VAT @ 20% (£)
                                </label>
                                <input
                                  type="number"
                                  step="0.01"
                                  value={formData.vat_amount || ''}
                                  className="w-full input"
                                  readOnly
                                />
                              </div>

                              {/* Total Cost */}
                              <div className="space-y-2">
                                <label className="label">
                                  <CalculatorIcon className="h-4 w-4 text-green-600" />
                                  Total Cost (£)
                                </label>
                                <input
                                  type="number"
                                  step="0.01"
                                  value={formData.total_cost}
                                  onChange={(e) => setFormData(prev => ({ ...prev, total_cost: e.target.value }))}
                                  className="w-full input"
                                  readOnly
                                />
                              </div>
                            </div>
                          </div>
                          </div>
                        </div>
                </div>

                {/* Price Comparison Footer */}
                {formData.price && formData.pricing_term && formData.hire_duration_days && (
                  <div className="mt-6 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl">
                    <div className="flex items-center gap-3 text-sm">
                      <CalculatorIcon className="h-5 w-5 text-blue-600" />
                      <span className="font-semibold text-blue-800">Price Comparison:</span>
                      <span className="text-blue-700 font-medium">
                        £{calculatePricePerDay(parseFloat(formData.price), formData.pricing_term, parseInt(formData.hire_duration_days)).toFixed(2)} per day
                      </span>
                    </div>
                  </div>
                )}

                {/* Footer Actions */}
                <div className="flex justify-between items-center pt-8 border-t border-gray-200">
                  <div className="flex-1">
                    {/* Price comparison moved here to prevent layout shift */}
                  </div>
                  <div className="flex gap-4">
                    <button
                      type="button"
                      onClick={() => setShowModal(false)}
                      className="btn btn-soft"
                    >
                      Close
                    </button>
                    <button
                      type="submit"
                      className="btn btn-primary"
                    >
                      {editingHire ? 'Update Hire' : 'Create Hire'}
                    </button>
                    {zaharaSettings && zaharaSettings.is_active && zaharaSettings.api_key && zaharaSettings.nominal_code_id && zaharaSettings.tax_code_id && editingHire && !editingHire.zahara_document_id && (
                    <button
                      type="button"
                      onClick={() => handleExportToZahara(editingHire.id)}
                      disabled={exportingToZahara === editingHire.id}
                      className="btn btn-secondary"
                    >
                      {exportingToZahara === editingHire.id ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                          Exporting...
                        </>
                      ) : (
                        <>
                          <WrenchScrewdriverIcon className="h-4 w-4" />
                          Export to Zahara
                        </>
                      )}
                    </button>
                  )}
                  {editingHire && editingHire.zahara_document_id && editingHire.zahara_document_id !== '' && (
                    <a
                      href={`https://myzahara.net/v3/documents/purchase-orders/view/${editingHire.zahara_document_id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
                    >
                      <WrenchScrewdriverIcon className="h-4 w-4" />
                      View PO #{editingHire.zahara_po_number || editingHire.zahara_document_id} in Zahara
                    </a>
                  )}
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Equipment Add Modal */}
      {showEquipmentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[60]">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <WrenchScrewdriverIcon className="h-6 w-6 text-blue-600" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900">Add New Equipment</h2>
                </div>
                <button
                  onClick={() => setShowEquipmentModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <form onSubmit={handleSubmitEquipment} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="label">
                      Name *
                    </label>
                    <input
                      type="text"
                      value={equipmentFormData.name}
                      onChange={(e) => setEquipmentFormData(prev => ({ ...prev, name: e.target.value }))}
                      className="w-full  input"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="label">
                      Category *
                    </label>
                    <select
                      value={equipmentFormData.category}
                      onChange={(e) => setEquipmentFormData(prev => ({ ...prev, category: e.target.value }))}
                      className="w-full select"
                      required
                    >
                      <option value="">Select a category</option>
                      <option value="Access Equipment">Access Equipment</option>
                      <option value="Aircon & Cooling">Aircon & Cooling</option>
                      <option value="Breaking & Drilling">Breaking & Drilling</option>
                      <option value="Building & Sitework">Building & Sitework</option>
                      <option value="Cleaning">Cleaning</option>
                      <option value="Concreting & Compaction">Concreting & Compaction</option>
                      <option value="Decorating Tools & Equipment">Decorating Tools & Equipment</option>
                      <option value="Heating">Heating</option>
                      <option value="Lift & Shift">Lift & Shift</option>
                      <option value="Lighting">Lighting</option>
                      <option value="Outdoor & Gardening">Outdoor & Gardening</option>
                      <option value="Painting & Decorating">Painting & Decorating</option>
                      <option value="Plumbing & Pipework">Plumbing & Pipework</option>
                      <option value="Power">Power</option>
                      <option value="Power Access">Power Access</option>
                      <option value="Pumping & Drying">Pumping & Drying</option>
                      <option value="Sanding & Fixing">Sanding & Fixing</option>
                      <option value="Sawing & Cutting">Sawing & Cutting</option>
                      <option value="Welding">Welding</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="label">
                      Unit of Measure *
                    </label>
                    <select
                      value={equipmentFormData.unit_of_measure}
                      onChange={(e) => setEquipmentFormData(prev => ({ ...prev, unit_of_measure: e.target.value }))}
                      className="w-full  select"
                      required
                    >
                      <option value="">Select Unit</option>
                      <option value="Each">Each</option>
                      <option value="Per Day">Per Day</option>
                      <option value="Per Week">Per Week</option>
                      <option value="Per Month">Per Month</option>
                      <option value="Per Hour">Per Hour</option>
                      <option value="Per Metre">Per Metre</option>
                      <option value="Per Square Metre">Per Square Metre</option>
                      <option value="Per Cubic Metre">Per Cubic Metre</option>
                      <option value="Per Tonne">Per Tonne</option>
                      <option value="Per Litre">Per Litre</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="label">
                      Power Type
                    </label>
                    <select
                      value={equipmentFormData.power}
                      onChange={(e) => setEquipmentFormData(prev => ({ ...prev, power: e.target.value }))}
                      className="w-full  select"
                    >
                      <option value="">Select Power Type</option>
                      <option value="Petrol">Petrol</option>
                      <option value="Diesel">Diesel</option>
                      <option value="110V">110V</option>
                      <option value="240V">240V</option>
                      <option value="Battery">Battery</option>
                      <option value="Manual">Manual</option>
                    </select>
                  </div>
                </div>
                
                <div>
                  <label className="label">
                    Description *
                  </label>
                  <textarea
                    value={equipmentFormData.description}
                    onChange={(e) => setEquipmentFormData(prev => ({ ...prev, description: e.target.value }))}
                    rows={3}
                    className="w-full  input"
                    required
                  />
                </div>
                
                <div>
                  <label className="label">
                    Image
                  </label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => setEquipmentFormData(prev => ({ ...prev, image: e.target.files?.[0] || null }))}
                    className="w-full input"
                  />
                </div>
                
                <div className="flex justify-end gap-4 pt-6">
                  <button
                    type="button"
                    onClick={() => setShowEquipmentModal(false)}
                    className="btn btn-close"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                  >
                    <WrenchScrewdriverIcon className="h-4 w-4" />
                    Create Equipment
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Supplier Add Modal */}
      {showSupplierModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[60]">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <BuildingOfficeIcon className="h-6 w-6 text-blue-600" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900">Add New Supplier</h2>
                </div>
                <button
                  onClick={() => setShowSupplierModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <form onSubmit={handleSubmitSupplier} className="space-y-6">
                <div>
                  <label className="label">
                    Supplier Name *
                  </label>
                  <input
                    type="text"
                    value={supplierFormData.name}
                    onChange={(e) => setSupplierFormData(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full  input"
                    required
                  />
                </div>
                
                <div>
                  <label className="label">
                    Contact Email
                  </label>
                  <input
                    type="email"
                    value={supplierFormData.contact_email}
                    onChange={(e) => setSupplierFormData(prev => ({ ...prev, contact_email: e.target.value }))}
                    className="w-full  input"
                  />
                </div>
                
                <div>
                  <label className="label">
                    Contact Phone
                  </label>
                  <input
                    type="tel"
                    value={supplierFormData.contact_phone}
                    onChange={(e) => setSupplierFormData(prev => ({ ...prev, contact_phone: e.target.value }))}
                    className="w-full  input"
                  />
                </div>
                
                <div>
                  <label className="label">
                    Address
                  </label>
                  <textarea
                    value={supplierFormData.address}
                    onChange={(e) => setSupplierFormData(prev => ({ ...prev, address: e.target.value }))}
                    rows={3}
                    className="w-full  input"
                  />
                </div>
                
                <div className="flex justify-end gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowSupplierModal(false)}
                    className="btn btn-soft"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                  >
                    <BuildingOfficeIcon className="h-4 w-4" />
                    Create Supplier
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Site Add Modal */}
      {showSiteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[60]">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <MapPinIcon className="h-6 w-6 text-blue-600" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900">Add New Site</h2>
                </div>
                <button
                  onClick={() => setShowSiteModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <form onSubmit={handleSubmitSite} className="space-y-6">
                <div>
                  <label className="label">
                    Site Name *
                  </label>
                  <input
                    type="text"
                    value={siteFormData.name}
                    onChange={(e) => setSiteFormData(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full  input"
                    required
                  />
                </div>
                
                <div>
                  <label className="label">
                    Job Number
                  </label>
                  <input
                    type="text"
                    value={siteFormData.job_number}
                    onChange={(e) => setSiteFormData(prev => ({ ...prev, job_number: e.target.value }))}
                    className="w-full  input"
                  />
                </div>
                
                <div>
                  <label className="label">
                    Address Line 1 *
                  </label>
                  <input
                    type="text"
                    value={siteFormData.address_line_1}
                    onChange={(e) => setSiteFormData(prev => ({ ...prev, address_line_1: e.target.value }))}
                    className="w-full  input"
                    required
                  />
                </div>
                
                <div>
                  <label className="label">
                    Address Line 2
                  </label>
                  <input
                    type="text"
                    value={siteFormData.address_line_2}
                    onChange={(e) => setSiteFormData(prev => ({ ...prev, address_line_2: e.target.value }))}
                    className="w-full  input"
                  />
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="label">
                      City *
                    </label>
                    <input
                      type="text"
                      value={siteFormData.city}
                      onChange={(e) => setSiteFormData(prev => ({ ...prev, city: e.target.value }))}
                      className="w-full  input"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="label">
                      County
                    </label>
                    <input
                      type="text"
                      value={siteFormData.county}
                      onChange={(e) => setSiteFormData(prev => ({ ...prev, county: e.target.value }))}
                      className="w-full  input"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="label">
                      Postcode *
                    </label>
                    <input
                      type="text"
                      value={siteFormData.postcode}
                      onChange={(e) => setSiteFormData(prev => ({ ...prev, postcode: e.target.value }))}
                      className="w-full  input"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="label">
                      Country *
                    </label>
                    <select
                      value={siteFormData.country}
                      onChange={(e) => setSiteFormData(prev => ({ ...prev, country: e.target.value }))}
                      className="w-full  select"
                      required
                    >
                      <option value="GB">United Kingdom</option>
                      <option value="US">United States</option>
                      <option value="CA">Canada</option>
                      <option value="AU">Australia</option>
                      <option value="IE">Ireland</option>
                    </select>
                  </div>
                </div>
                
                <div>
                  <label className="label">
                    Status *
                  </label>
                  <select
                    value={siteFormData.status}
                    onChange={(e) => setSiteFormData(prev => ({ ...prev, status: e.target.value as 'active' | 'inactive' | 'completed' }))}
                    className="w-full select"
                    required
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="completed">Completed</option>
                  </select>
                </div>
                
                <div className="flex justify-end gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowSiteModal(false)}
                    className="btn btn-soft"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                  >
                    <MapPinIcon className="h-4 w-4" />
                    Create Site
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-8 max-w-md w-full mx-4">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
                <svg className="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Success!</h3>
              <p className="text-sm text-gray-500 mb-6">{successMessage}</p>
              <button
                onClick={() => {
                  console.log('Success modal continue clicked');
                  setShowSuccessModal(false);
                  setRefreshTrigger(prev => {
                    console.log('Setting refresh trigger to:', prev + 1);
                    return prev + 1;
                  });
                }}
                className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Purchase Order Confirmation Modal */}
      {showPurchaseOrderModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-8 max-w-md w-full mx-4">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 mb-4">
                <svg className="h-6 w-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Hire Created Successfully!</h3>
              <p className="text-sm text-gray-500 mb-6">
                As you are integrated with Zahara, would you like a Purchase Order creating?
                {selectedSupplier && selectedSupplier.contact_email && (
                  <span className="block mt-2 text-blue-600">
                    You can also send a booking email to {selectedSupplier.name}
                  </span>
                )}
              </p>
              <div className="space-y-3">
                <div className="flex gap-3">
                  <button
                    onClick={handlePurchaseOrderDecline}
                    className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                  >
                    No, Thanks
                  </button>
                  <button
                    onClick={handlePurchaseOrderConfirm}
                    disabled={exportingToZahara === newHireId}
                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {exportingToZahara === newHireId ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        Creating...
                      </>
                    ) : (
                      'Yes, Create PO'
                    )}
                  </button>
                </div>
                {selectedSupplier && selectedSupplier.contact_email && (
                  <button
                    onClick={() => {
                      setShowPurchaseOrderModal(false);
                      setShowEmailModal(true);
                    }}
                    className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 flex items-center justify-center gap-2"
                  >
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    Send Booking Email
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Email Booking Modal */}
      {showEmailModal && selectedSupplier && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-8 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="text-center mb-6">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
                <svg className="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">Send Booking Email</h3>
                <p className="text-sm text-gray-500 mb-6">
                  Send a professional booking email to {selectedSupplier.name}
                  {selectedHires.length > 1 && (
                    <span className="ml-2 text-indigo-600 font-medium">
                      ({selectedHires.length} items selected)
                    </span>
                  )}
                </p>
                {process.env.NODE_ENV === 'development' && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-6">
                    <div className="flex items-center">
                      <svg className="h-5 w-5 text-yellow-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                      </svg>
                      <span className="text-sm text-yellow-800 font-medium">Test Mode:</span>
                      <span className="text-sm text-yellow-700 ml-2">All emails will be sent to mp@myzahara.net</span>
                    </div>
                  </div>
                )}
            </div>

            <div className="space-y-4 mb-6">
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowEmailModal(false);
                    setShowSuccessModal(true);
                    setSuccessMessage('Hire created successfully!');
                  }}
                  className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                >
                  Skip Email
                </button>
                <button
                  onClick={async (e) => {
                    try {
                      // Determine which hires to send
                      const hireIds = newHireId ? [newHireId] : selectedHires;
                      
                      if (hireIds.length === 0) {
                        alert('No hires selected to send email for');
                        return;
                      }

                      // Show loading state
                      const button = e.target as HTMLButtonElement;
                      const originalText = button.textContent;
                      button.textContent = 'Sending...';
                      button.disabled = true;

                      // Send the email
                      const response = await hiresAPI.sendBookingEmail(hireIds, selectedSupplier.id);
                      
                      // Show success message
                      alert(`Booking email sent successfully to ${response.data.toEmail}!`);
                      
                      // Close the modal
                      setShowEmailModal(false);
                      setSelectedSupplier(null);
                      
                    } catch (error: any) {
                      console.error('Error sending booking email:', error);
                      alert(`Failed to send booking email: ${error.response?.data?.error || error.message}`);
                      
                      // Reset button state
                      const button = e.target as HTMLButtonElement;
                      button.textContent = 'Send from Planted';
                      button.disabled = false;
                    }
                  }}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Send from Planted
                </button>
                  <button
                    onClick={async () => {
                      try {
                        // Get the hire data to populate the template
                        const hireData = newHireId ? 
                          [hires.find(h => h.id === newHireId)] : 
                          selectedHires.map(hireId => hires.find(h => h.id === hireId)).filter(Boolean);
                        
                        if (!hireData || hireData.length === 0) {
                          alert('Hire data not found. Please try again.');
                          return;
                        }

                        const cleanCustomerName = customer?.name?.replace(/[^a-zA-Z0-9]/g, '') || 'Customer';
                        const isDevelopment = process.env.NODE_ENV === 'development';
                        const toEmail = isDevelopment ? 'mp@myzahara.net' : selectedSupplier.contact_email;
                        
                        // Generate equipment list for multiple hires
                        const equipmentList = hireData.map(hire => 
                          `• ${hire?.equipment_name || 'Unknown Equipment'} - ${hire?.hire_start_date ? new Date(hire.hire_start_date).toLocaleDateString('en-GB') : 'N/A'} to ${hire?.hire_end_date ? new Date(hire.hire_end_date).toLocaleDateString('en-GB') : 'N/A'} (£${hire?.price_per_day || hire?.price || 0}/day, Total: £${hire?.total_cost || 0})`
                        ).join('\n');
                        
                        const totalCost = hireData.reduce((sum, hire) => sum + (hire?.total_cost || 0), 0);
                        const subject = hireData.length === 1 ? 
                          `Equipment Hire Booking - ${hireData[0]?.equipment_name || 'Unknown Equipment'}` : 
                          `Equipment Hire Booking - ${hireData.length} Items`;
                        
                        const emailTemplate = `To: ${toEmail}
From: ${cleanCustomerName}@plantedhire.com
Subject: ${subject}

Dear ${selectedSupplier.name},

We would like to book the following equipment:

${equipmentList}

Total Cost: £${totalCost.toFixed(2)}
Site: ${hireData[0]?.site_name || 'Unknown Site'}
Delivery Address: ${hireData[0]?.site_address || hireData[0]?.site_address_line_1 || 'Please provide delivery address'}

Please confirm availability and provide delivery details.

Best regards,
${customer?.name}`;

                        await navigator.clipboard.writeText(emailTemplate);
                        alert('Email template with hire details copied to clipboard!');
                      } catch (error) {
                        console.error('Failed to generate email:', error);
                        alert('Failed to generate email. Please try again.');
                      }
                    }}
                    className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
                  >
                    Generate Email
                  </button>
              </div>
            </div>

            {/* Email Template Preview */}
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <h4 className="text-sm font-medium text-gray-700 mb-3">Email Template Preview:</h4>
              <div className="bg-white rounded border p-4 text-left text-sm">
                <div className="space-y-2">
                  <div><strong>To:</strong> {process.env.NODE_ENV === 'development' ? 'mp@myzahara.net' : selectedSupplier.contact_email}</div>
                  <div><strong>From:</strong> {customer?.name?.replace(/[^a-zA-Z0-9]/g, '') || 'Customer'}@plantedhire.com</div>
                  <div><strong>Subject:</strong> {(() => {
                    const hireData = newHireId ? 
                      [hires.find(h => h.id === newHireId)] : 
                      selectedHires.map(hireId => hires.find(h => h.id === hireId)).filter(Boolean);
                    return hireData.length === 1 ? 
                      `Equipment Hire Booking - ${hireData[0]?.equipment_name || '[Equipment Name]'}` : 
                      `Equipment Hire Booking - ${hireData.length} Items`;
                  })()}</div>
                  <div className="mt-4">
                    <p>Dear {selectedSupplier.name},</p>
                    <p className="mt-2">
                      We would like to book the following equipment:
                    </p>
                    <div className="mt-3 bg-gray-50 p-3 rounded">
                      {(() => {
                        const hireData = newHireId ? 
                          [hires.find(h => h.id === newHireId)] : 
                          selectedHires.map(hireId => hires.find(h => h.id === hireId)).filter(Boolean);
                        
                        if (hireData.length === 1) {
                          const hire = hireData[0];
                          return (
                            <>
                              <p><strong>Equipment:</strong> {hire?.equipment_name || '[Equipment Name]'}</p>
                              <p><strong>Hire Period:</strong> {hire ? `${new Date(hire.hire_start_date).toLocaleDateString('en-GB')} to ${new Date(hire.hire_end_date).toLocaleDateString('en-GB')}` : '[Start Date] to [End Date]'}</p>
                              <p><strong>Daily Rate:</strong> £{hire?.price_per_day || hire?.price || '[Price per Day]'}</p>
                              <p><strong>Total Cost:</strong> £{hire?.total_cost || '[Total Cost]'}</p>
                              <p><strong>Site:</strong> {hire?.site_name || '[Site Name]'}</p>
                              <p><strong>Delivery Address:</strong> {hire?.site_address || hire?.site_address_line_1 || '[Site Address]'}</p>
                            </>
                          );
                        } else {
                          const totalCost = hireData.reduce((sum, hire) => sum + (hire?.total_cost || 0), 0);
                          return (
                            <>
                              {hireData.map((hire, index) => (
                                <p key={index}><strong>• {hire?.equipment_name}</strong> - {hire ? `${new Date(hire.hire_start_date).toLocaleDateString('en-GB')} to ${new Date(hire.hire_end_date).toLocaleDateString('en-GB')}` : '[Dates]'} (£{hire?.price_per_day || hire?.price || '[Price]'}/day, Total: £{hire?.total_cost || '[Cost]'})</p>
                              ))}
                              <p className="mt-2"><strong>Total Cost:</strong> £{totalCost.toFixed(2)}</p>
                              <p><strong>Site:</strong> {hireData[0]?.site_name || '[Site Name]'}</p>
                              <p><strong>Delivery Address:</strong> {hireData[0]?.site_address || hireData[0]?.site_address_line_1 || '[Site Address]'}</p>
                            </>
                          );
                        }
                      })()}
                    </div>
                    <p className="mt-3">
                      Please confirm availability and provide delivery details.
                    </p>
                    <p className="mt-2">
                      Best regards,<br/>
                      {customer?.name}
                    </p>
                  </div>
                </div>
              </div>
              <div className="mt-3 flex justify-end">
                  <button
                    onClick={async () => {
                      try {
                        const cleanCustomerName = customer?.name?.replace(/[^a-zA-Z0-9]/g, '') || 'Customer';
                        const isDevelopment = process.env.NODE_ENV === 'development';
                        const toEmail = isDevelopment ? 'mp@myzahara.net' : selectedSupplier.contact_email;
                        const emailTemplate = `To: ${toEmail}
From: ${cleanCustomerName}@plantedhire.com
Subject: Equipment Hire Booking - [Equipment Name]

Dear ${selectedSupplier.name},

We would like to book the following equipment:

Equipment: [Equipment Name]
Hire Period: [Start Date] to [End Date]
Daily Rate: £[Price per Day]
Total Cost: £[Total Cost]
Site: [Site Name]
Delivery Address: [Site Address]

Please confirm availability and provide delivery details.

Best regards,
${customer?.name}`;

                        await navigator.clipboard.writeText(emailTemplate);
                        alert('Email template copied to clipboard!');
                      } catch (error) {
                        console.error('Failed to copy to clipboard:', error);
                        alert('Failed to copy to clipboard. Please try again.');
                      }
                    }}
                    className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center gap-2"
                  >
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                    Copy Template
                  </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Order Confirmation Modal */}
      {showBulkOrderModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 max-w-lg w-full mx-4">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 mb-4">
                <svg className="h-6 w-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Create Bulk Purchase Order</h3>
              <p className="text-sm text-gray-500 mb-4">
                You have selected {selectedHires.length} hire{selectedHires.length > 1 ? 's' : ''} from the same supplier.
              </p>
              
              {/* Show selected hires summary */}
              <div className="bg-gray-50 rounded-lg p-4 mb-6 max-h-40 overflow-y-auto">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Selected Hires:</h4>
                <div className="space-y-1">
                  {selectedHires.map(hireId => {
                    const hire = hires.find(h => h.id === hireId);
                    return hire ? (
                      <div key={hireId} className="text-xs text-gray-600 flex justify-between">
                        <span>{hire.equipment_name}</span>
                        <span className="font-medium">£{hire.total_cost}</span>
                      </div>
                    ) : null;
                  })}
                </div>
              </div>

              <p className="text-sm text-gray-500 mb-6">
                This will create a single purchase order with multiple line items. Each hire will be a separate line item linked to its respective site's Zahara project.
              </p>
              
              <div className="flex gap-3">
                <button
                  onClick={() => setShowBulkOrderModal(false)}
                  className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                >
                  Cancel
                </button>
                <button
                  onClick={handleBulkOrderConfirm}
                  disabled={exportingToZahara !== null}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {exportingToZahara ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Creating...
                    </>
                  ) : (
                    `Create Bulk PO (${selectedHires.length} items)`
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Status Update Confirmation Modal */}
      {showBulkStatusModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-orange-100 mb-4">
                <svg className="h-6 w-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Update Status</h3>
              <p className="text-sm text-gray-500 mb-6">
                Are you sure you want to mark {selectedHires.length} hire{selectedHires.length > 1 ? 's' : ''} as <strong>{bulkStatusAction ? formatStatusDisplay(bulkStatusAction) : ''}</strong>?
              </p>
              
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowBulkStatusModal(false);
                    setBulkStatusAction(null);
                  }}
                  className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                >
                  Cancel
                </button>
                <button
                  onClick={handleBulkStatusConfirm}
                  className={`flex-1 px-4 py-2 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                    bulkStatusAction === 'completed' 
                      ? 'bg-green-600 hover:bg-green-700 focus:ring-green-500' 
                      : 'bg-orange-600 hover:bg-orange-700 focus:ring-orange-500'
                  }`}
                >
                  Yes, Mark as {bulkStatusAction ? formatStatusDisplay(bulkStatusAction) : ''}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Extend Hire Modal */}
      {showExtendModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-purple-100 mb-4">
                <svg className="h-6 w-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Extend Hire</h3>
              <p className="text-sm text-gray-500 mb-6">
                Extend {selectedHires.length} hire{selectedHires.length > 1 ? 's' : ''} to what date?
              </p>
              
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  New End Date
                </label>
                <input
                  type="date"
                  value={extendDate}
                  onChange={(e) => setExtendDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  min={new Date().toISOString().split('T')[0]} // Today's date as minimum
                />
              </div>

              {/* Extension Calculations Preview */}
              {extendDate && (
                <div className="mb-6 bg-gray-50 rounded-lg p-4 text-left">
                  <h4 className="text-sm font-medium text-gray-700 mb-3">Extension Preview:</h4>
                  <div className="space-y-3">
                    {selectedHires.map(hireId => {
                      const hire = hires.find(h => h.id === hireId);
                      if (!hire) return null;

                      const originalStartDate = new Date(hire.hire_start_date);
                      const originalEndDate = new Date(hire.hire_end_date);
                      const newEndDate = new Date(extendDate);
                      
                      // Calculate extension days
                      const extensionDays = Math.ceil((newEndDate.getTime() - originalEndDate.getTime()) / (1000 * 60 * 60 * 24));
                      
                      // Calculate extension cost
                      const pricePerDay = parseFloat(String(hire.price_per_day || hire.price || '0'));
                      const extensionCost = extensionDays * pricePerDay;
                      const extensionCostWithVAT = extensionCost * (1 + VAT_RATE);

                      return (
                        <div key={hireId} className="bg-white rounded-lg p-3 border border-gray-200">
                          <div className="flex justify-between items-start mb-2">
                            <div>
                              <div className="font-medium text-gray-900">{hire.equipment_name}</div>
                              <div className="text-sm text-gray-600">{hire.site_name}</div>
                            </div>
                            <div className="text-right">
                              <div className="text-sm font-medium text-gray-900">£{extensionCost.toFixed(2)}</div>
                              <div className="text-xs text-gray-500">+ £{(extensionCostWithVAT - extensionCost).toFixed(2)} VAT</div>
                            </div>
                          </div>
                          
                          <div className="grid grid-cols-2 gap-2 text-xs text-gray-600">
                            <div>
                              <span className="font-medium">Original:</span><br />
                              {originalStartDate.toLocaleDateString()} → {originalEndDate.toLocaleDateString()}
                            </div>
                            <div>
                              <span className="font-medium">Extension:</span><br />
                              {originalEndDate.toLocaleDateString()} → {newEndDate.toLocaleDateString()}
                            </div>
                          </div>
                          
                          <div className="mt-2 pt-2 border-t border-gray-100">
                            <div className="flex justify-between text-xs">
                              <span className="text-gray-600">Extension Days:</span>
                              <span className="font-medium text-purple-600">{extensionDays} day{extensionDays !== 1 ? 's' : ''}</span>
                            </div>
                            <div className="flex justify-between text-xs">
                              <span className="text-gray-600">Price per Day:</span>
                              <span className="font-medium">£{pricePerDay.toFixed(2)}</span>
                            </div>
                            <div className="flex justify-between text-xs font-medium text-gray-900">
                              <span>Total Extension Cost:</span>
                              <span>£{extensionCostWithVAT.toFixed(2)}</span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    
                    {/* Total Summary */}
                    <div className="bg-purple-50 rounded-lg p-3 border border-purple-200">
                      <div className="flex justify-between items-center">
                        <span className="font-medium text-purple-900">Total Extension Cost:</span>
                        <span className="text-lg font-bold text-purple-900">
                          £{selectedHires.reduce((total, hireId) => {
                            const hire = hires.find(h => h.id === hireId);
                            if (!hire) return total;
                            
                            const originalEndDate = new Date(hire.hire_end_date);
                            const newEndDate = new Date(extendDate);
                            const extensionDays = Math.ceil((newEndDate.getTime() - originalEndDate.getTime()) / (1000 * 60 * 60 * 24));
                            const pricePerDay = parseFloat(String(hire.price_per_day || hire.price || '0'));
                            const extensionCost = extensionDays * pricePerDay;
                            const extensionCostWithVAT = extensionCost * (1 + VAT_RATE);
                            
                            return total + extensionCostWithVAT;
                          }, 0).toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowExtendModal(false);
                    setExtendDate('');
                  }}
                  className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                >
                  Cancel
                </button>
                <button
                  onClick={handleExtendConfirm}
                  disabled={!extendDate}
                  className="flex-1 px-4 py-2 text-white bg-purple-600 rounded-lg hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  Extend Hire
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Zahara Extension Choice Modal */}
      {showZaharaExtensionModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 mb-4">
                <svg className="h-6 w-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Purchase Order Extension</h3>
              <p className="text-sm text-gray-500 mb-6">
                Some of the selected hires have existing Zahara purchase orders. How would you like to handle the extension?
              </p>
              
              <div className="space-y-3 mb-6">
                <button
                  onClick={() => performHireExtension('new_order')}
                  disabled={isExtendingHire}
                  className="w-full px-4 py-3 text-left bg-green-50 border border-green-200 rounded-lg hover:bg-green-100 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <div className="flex items-center gap-3">
                    {isExtendingHire ? (
                      <svg className="h-5 w-5 text-green-600 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    ) : (
                      <svg className="h-5 w-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                      </svg>
                    )}
                    <div>
                      <div className="font-medium text-green-900">
                        {isExtendingHire ? 'Creating Purchase Order...' : 'Create New Purchase Order'}
                      </div>
                      <div className="text-sm text-green-700">
                        {isExtendingHire ? 'Please wait while we process your request' : 'Generate a separate PO for the extension period'}
                      </div>
                    </div>
                  </div>
                </button>
                
                <button
                  onClick={() => performHireExtension('update_existing')}
                  disabled={isExtendingHire}
                  className="w-full px-4 py-3 text-left bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <div className="flex items-center gap-3">
                    {isExtendingHire ? (
                      <svg className="h-5 w-5 text-blue-600 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    ) : (
                      <svg className="h-5 w-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    )}
                    <div>
                      <div className="font-medium text-blue-900">
                        {isExtendingHire ? 'Updating Purchase Order...' : 'Update Existing Purchase Order'}
                      </div>
                      <div className="text-sm text-blue-700">
                        {isExtendingHire ? 'Please wait while we process your request' : 'Add extension line item to existing PO'}
                      </div>
                    </div>
                  </div>
                </button>
              </div>
              
              <button
                onClick={() => {
                  setShowZaharaExtensionModal(false);
                  setShowExtendModal(false);
                  setExtendDate('');
                }}
                disabled={isExtendingHire}
                className="w-full px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Delete Confirmation Modal */}
      {showBulkDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
                <svg className="h-6 w-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Delete Hires</h3>
              <p className="text-sm text-gray-500 mb-4">
                Are you sure you want to delete {selectedHires.length} hire{selectedHires.length > 1 ? 's' : ''}? This action cannot be undone.
              </p>
              
              {/* Show selected hires summary */}
              <div className="bg-gray-50 rounded-lg p-4 mb-6 max-h-40 overflow-y-auto">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Selected Hires:</h4>
                <div className="space-y-1">
                  {selectedHires.map(hireId => {
                    const hire = hires.find(h => h.id === hireId);
                    return hire ? (
                      <div key={hireId} className="text-xs text-gray-600 flex justify-between">
                        <span>{hire.equipment_name}</span>
                        <span className="font-medium">£{hire.total_cost}</span>
                      </div>
                    ) : null;
                  })}
                </div>
              </div>
              
              <div className="flex gap-3">
                <button
                  onClick={() => setShowBulkDeleteModal(false)}
                  className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                >
                  Cancel
                </button>
                <button
                  onClick={handleBulkDeleteConfirm}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                >
                  Yes, Delete {selectedHires.length} Hire{selectedHires.length > 1 ? 's' : ''}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Upgrade Required Modal */}
      {showUpgradeModal && subscriptionUsage && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-8 max-w-md w-full mx-4 shadow-2xl">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-gradient-to-r from-blue-100 to-purple-100 mb-6">
                <svg className="h-8 w-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">Upgrade Required</h3>
              <p className="text-gray-600 mb-6">
                You've reached the maximum of <span className="font-semibold text-blue-600">{subscriptionUsage.usage.hires.current}</span> active hires on your <span className="font-semibold text-blue-600">{subscriptionUsage.plan}</span> plan.
              </p>
              <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg p-4 mb-6">
                <p className="text-sm text-gray-700 mb-2">Current Usage:</p>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Active Hires:</span>
                  <span className="font-semibold text-blue-600">{subscriptionUsage.usage.hires.current}/{subscriptionUsage.usage.hires.max}</span>
                </div>
              </div>
              <p className="text-gray-600 mb-6">
                Upgrade your plan to create more hires and unlock additional features.
              </p>
              <div className="flex space-x-3">
                <button
                  onClick={() => setShowUpgradeModal(false)}
                  className="flex-1 px-4 py-3 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors"
                >
                  Maybe Later
                </button>
                <button
                  onClick={() => {
                    setShowUpgradeModal(false);
                    // Navigate to subscription page
                    window.location.href = '/subscription';
                  }}
                  className="flex-1 px-4 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all transform hover:scale-105"
                >
                  Upgrade Now
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Hires;